/**
 * THE ANSWER — prose as PLAIN TEXT, every number from the structured result.
 *
 * ══ THERE IS NO MARKDOWN RENDERER ON THIS SCREEN, AND THERE MUST NEVER BE ══
 *
 * 🔴 Markdown image auto-fetch is the verified exfiltration channel in three
 * recent incidents — EchoLeak (CVE-2025-32711), GrafanaGhost, Superhuman — and
 * TWO of them passed a URL allowlist. The admin panel today has ZERO
 * `dangerouslySetInnerHTML` and no sanitizer anywhere in the tree, and this
 * feature preserves that property rather than spending it. Assistant prose is a
 * React TEXT CHILD; a table is a table; a chart is inline SVG from recharts.
 * `tests/unit/lint/agent-chat-no-remote-render.test.ts` walks this whole
 * directory as a GLOB, so a file added later cannot be born unfenced.
 *
 * ══ ONE VIEW ON THE SCREEN AT A TIME (C13) ═════════════════════════════════
 *
 * Prose AND table both render `model_view` by default, so the assistant saying
 * "suppressed, fewer than 11" beside a table showing 3 cannot happen — there is
 * nothing else on screen to disagree with. `PrecisionToggle` swaps BOTH to
 * `ui_view` and pins a standing banner (`precisionBanner`) that says whether the
 * model received these same figures — since the owner's decision of 2026-09-26
 * it usually did. What makes the toggle safe is not scarcity — the admin can read
 * these numbers on 79 existing pages — but that the revealed value cannot
 * travel back into the model: `TurnRequest` has two fields and no parameter to
 * carry one.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Clock, Server, Users } from 'lucide-react';

import type {
  MetricResultEnvelope,
  Precision,
  SqlQueryInfo,
  ToolResultRecord,
  TurnResultPayload,
} from '../protocol';
import { envelopesOfTurn, readToolPresentation } from '../protocol';
import {
  chooseChart,
  emptyResultMessage,
  recordedSinceText,
  rendersInAnswer,
  deepLinkFor,
  foldProvenance,
  natBanner,
  precisionBanner,
  suppressionNotice,
  unavailableText,
} from '../answer-view';
import { AnswerCheckDiagnostics } from './AnswerCheckDiagnostics';
import { ResultChart } from './ResultChart';
import { RawTable, ResultTable } from './ResultTable';
import { SuppressionNotice } from './Cells';
import { DeepLink } from './DeepLink';
import { ToolTrail } from './ToolTimeline';

function Pill({
  icon: Icon,
  children,
  tone = 'neutral',
  title,
}: {
  icon: typeof Clock;
  children: React.ReactNode;
  tone?: 'neutral' | 'warn';
  title?: string;
}) {
  return (
    <span
      title={title}
      className={
        tone === 'warn'
          ? 'inline-flex items-center gap-1 rounded-full border border-yellow-500/50 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300'
          : 'inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground'
      }
    >
      <Icon className="h-3 w-3" aria-hidden />
      {children}
    </span>
  );
}

export function AnswerCard({
  result,
  buildEnvironment,
  actions,
  incomplete = false,
  lead,
}: {
  result: TurnResultPayload;
  buildEnvironment: string | undefined;
  actions?: React.ReactNode;
  /**
   * Shown IN PLACE of the prose. A declined turn that still served tables
   * passes its decline panel here: the decline message IS that turn's prose
   * (run-agent-turn copies it), so printing both would say it twice.
   */
  lead?: React.ReactNode;
  /**
   * The worker stopped on its deadline or iteration cap. The results below are
   * whatever finished first, not the whole answer. Defaults false so every
   * existing call site keeps its meaning.
   */
  incomplete?: boolean;
}) {
  // 🔑 FULL PRECISION IS THE DEFAULT (owner, 2026-09-16). The operator opens on
  // the first-party view with the real figures; the toggle switches to `model`,
  // which is what actually crossed to the processor.
  //
  // This is NOT a disclosure change. Suppression protects what leaves for the
  // PROCESSOR, not the authorised operator reading their own admin panel —
  // `ui_view` is built from the raw rows and was always one click away, behind
  // the same page permission. What crosses to the model is untouched: the tool
  // result is serialised from `model_view` and nothing else (toolResultJson).
  // The amber banner below keeps the two readings honest.
  const [precision, setPrecision] = useState<Precision>('ui');

  const envelopes = useMemo(
    () => envelopesOfTurn(result, precision),
    [result, precision],
  );
  const provenance = useMemo(
    () => foldProvenance(envelopes, buildEnvironment),
    [envelopes, buildEnvironment],
  );
  const nat = natBanner({
    environment: result.environment,
    egressProfile: result.egress_profile,
  });
  const notice = provenance === null ? null : suppressionNotice(provenance);
  // Chosen from what the MODEL received, whichever view is on screen.
  const banner = useMemo(() => precisionBanner(envelopesOfTurn(result, 'model')), [result]);

  // NO CARD. A chat answer is the page's own content, not an object sitting on
  // it — the border, fill and padding that used to wrap this made every reply
  // read as a database record. The blocks INSIDE it (tables, charts, notices)
  // keep their own borders, so structure still reads; what goes away is the
  // outer frame around the prose. A JSX comment cannot sit here: `return (`
  // takes ONE root element, so the comment belongs in JS, above it.
  return (
    <article className="space-y-5">
      {/* ── THE PROVENANCE STRIP. Three fields, and each one is here because
             its absence has cost this estate something real. */}
      <header className="flex flex-wrap items-center gap-2">
        <Pill
          icon={Server}
          tone={provenance?.environmentMismatch ? 'warn' : 'neutral'}
          title={
            provenance?.environmentMismatch
              ? 'This answer was computed in a DIFFERENT environment from the one this panel is built for.'
              : undefined
          }
        >
          {result.environment}
          {provenance?.environmentMismatch ? ' ≠ panel' : ''}
        </Pill>
        {provenance?.asOf && (
          <Pill icon={Clock} tone={provenance.stale ? 'warn' : 'neutral'} title="Source watermark, never a data extremum.">
            {provenance.stale ? 'Stale · ' : 'as of '}
            {provenance.asOf}
          </Pill>
        )}
        {provenance?.populationId && (
          <Pill icon={Users} title={provenance.populationPredicate ?? undefined}>
            {provenance.populationId}
          </Pill>
        )}
        {banner !== null && (
          <span className="ml-auto">
            <PrecisionToggle precision={precision} onChange={setPrecision} />
          </span>
        )}
      </header>

      {precision === 'ui' && banner !== null && (
        <p
          className={
            banner.tone === 'warn'
              ? 'rounded-md border border-yellow-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300'
              : 'rounded-md border bg-muted/60 p-2 text-xs text-muted-foreground'
          }
          role="note"
        >
          {banner.text}
        </p>
      )}

      {nat !== null && (
        <p className="rounded-md border bg-muted/60 p-2 text-xs text-muted-foreground" role="note">
          {nat.message}
        </p>
      )}

      {/* ── THE PROSE. A text child. Nothing parses it. */}
      {lead}

      {lead === undefined && result.prose && (
        <p className="whitespace-pre-wrap text-[15px] leading-7 text-foreground">
          {result.prose}
        </p>
      )}

      {result.prose_fidelity_replaced && (
        <p
          className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300"
          role="note"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          The summary failed a numeric-fidelity check and was replaced. The tables below are
          the answer.
        </p>
      )}

      {result.answer_check && <AnswerCheckDiagnostics check={result.answer_check} />}

      {notice && <SuppressionNotice text={notice} />}

      {/* ── THE STRUCTURED RESULT, one block per tool call. */}
      <div className="space-y-5">
        {result.results.map((record, index) => (
          <ToolResultBlock
            key={`${record.tool}-${index}`}
            record={record}
            precision={precision}
          />
        ))}
      </div>

      {provenance?.populationPredicate && (
        <details className="rounded-md border bg-muted/30 p-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium">Who was counted</summary>
          <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
            {provenance.populationPredicate}
          </p>
        </details>
      )}

      <footer className="flex flex-wrap items-center gap-3 border-t pt-3">
        <ToolTrail tools={result.results.map((record) => record.tool)} />
        <span className="ml-auto text-[11px] text-muted-foreground">
          {result.iterations} iteration{result.iterations === 1 ? '' : 's'} ·{' '}
          {Math.round(result.duration_ms / 1000)} s
        </span>
      </footer>

      {incomplete && (
        <p
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300"
          role="note"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            This turn stopped before it finished, so the results below are
            <strong className="font-semibold"> partial</strong>. Ask a narrower
            version of the question for a complete answer.
          </span>
        </p>
      )}

      {actions}

      {/* ── ONE announcement on completion. The conversation log is
             `aria-live="polite"`; announcing every streamed token would flood a
             screen reader, which is why the running text is aria-hidden. */}
      <p className="sr-only" role="status">
        {incomplete ? 'Answer incomplete — the turn stopped before it finished.' : 'Answer complete.'}{' '}
        {envelopes.length} result{envelopes.length === 1 ? '' : 's'}.{' '}
        {provenance?.cellsSuppressed ?? 0} cells hidden.
      </p>
    </article>
  );
}

function PrecisionToggle({
  precision,
  onChange,
}: {
  precision: Precision;
  onChange: (next: Precision) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border text-[11px]" role="group" aria-label="Precision">
      <button
        type="button"
        aria-pressed={precision === 'model'}
        onClick={() => onChange('model')}
        className={`px-2 py-1 ${precision === 'model' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
      >
        What the model saw
      </button>
      <button
        type="button"
        aria-pressed={precision === 'ui'}
        onClick={() => onChange('ui')}
        className={`px-2 py-1 ${precision === 'ui' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
      >
        Full precision
      </button>
    </div>
  );
}

/**
 * The statement behind a `database_query` table, as plain text (never HTML). The
 * operator sees exactly what the agent read (owner decision 2026-10-15).
 */
function SqlQueryBlock({ query, rowCount }: { query: SqlQueryInfo; rowCount: number }) {
  return (
    <details className="rounded-md border bg-muted/30 p-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        SQL query · {rowCount} {rowCount === 1 ? 'row' : 'rows'} · read-only
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">
        {query.sql}
      </pre>
    </details>
  );
}

function EnvelopeBlock({
  envelope,
  label,
  query,
}: {
  envelope: MetricResultEnvelope;
  label: string | null;
  query?: SqlQueryInfo | null;
}) {
  const chart = chooseChart(envelope);
  const heading = label ?? (query ? 'Database query' : envelope.metric_id);
  // The CITATION comes from the envelope itself: `definition_url` IS the
  // metric's declared `deep_link.href`. No client-side catalogue, no guess.
  const link = deepLinkFor(envelope.definition_url);
  const unavailable = unavailableText(envelope);
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <h4 className="text-sm font-semibold">{heading}</h4>
        {!query && (
          <span className="font-mono text-[11px] text-muted-foreground">
            {envelope.metric_id} v{envelope.metric_version}
          </span>
        )}
        {link && <DeepLink to={link.href} label={link.label} />}
      </div>
      {query?.purpose && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">The agent&apos;s description:</span> {query.purpose}
        </p>
      )}
      {query && <SqlQueryBlock query={query} rowCount={envelope.rows.length} />}
      {/* True with rows and without: the newest collection failed or is stale, and
          the earlier days' figures are still real (unavailableText, #2964 c5). */}
      {unavailable && (
        <p className="rounded-md border bg-muted/50 p-2 text-xs text-muted-foreground" role="note">
          {!unavailable.hasFigures && <span className="mr-1 text-lg leading-none">—</span>}
          {unavailable.text}
        </p>
      )}
      {/* Where the source's records begin. A period NOTHING was recorded in, with
          no rows, says it in the empty table's own message (emptyResultMessage);
          every other case says it here, a part-recorded period with no rows too. */}
      {(envelope.rows.length > 0 || envelope.recorded_since?.covers === 'part') && recordedSinceText(envelope) && (
        <p className="rounded-md border bg-muted/50 p-2 text-xs text-muted-foreground" role="note">
          {recordedSinceText(envelope)}
        </p>
      )}
      {chart && <ResultChart plan={chart} title={heading} />}
      <ResultTable
        columns={envelope.columns}
        rows={envelope.rows}
        caption={`${envelope.metric_id} results`}
        emptyMessage={emptyResultMessage(envelope)?.text}
      />
    </section>
  );
}

function ToolResultBlock({
  record,
  precision,
}: {
  record: ToolResultRecord;
  precision: Precision;
}) {
  const presentation = readToolPresentation(record, precision);
  // How the agent FOUND the answer is not the answer (see rendersInAnswer).
  if (!rendersInAnswer(presentation.kind)) return null;

  switch (presentation.kind) {
    case 'envelopes':
      return (
        <div className="space-y-5">
          {presentation.envelopes.map((slot, index) => (
            <EnvelopeBlock
              key={`${slot.envelope.metric_id}-${index}`}
              envelope={slot.envelope}
              label={slot.label}
              query={slot.query}
            />
          ))}
          {presentation.delta && (
            <section className="space-y-2">
              <h4 className="text-sm font-semibold">Difference</h4>
              {presentation.delta.note && (
                <p className="text-xs text-muted-foreground">{presentation.delta.note}</p>
              )}
              {/* 🔴 THE DEFERRAL WAS WRONG. The previous commit said carrying the unit
                  here "needs a backend change". It does not: every key in
                  `delta.rows` is BY CONSTRUCTION a key of window_a's columns —
                  metric-compare.ts:84-86 copies dimension and time cells as
                  `out[column.key] = rowA[column.key]` while iterating `a.columns`,
                  and :71 takes the measure keys from `measureKeysOf(a)`. Both
                  envelopes and the delta arrive in ONE presentation object, so
                  the specs were one line away. Without them a revenue delta of
                  249900 cents printed `249,900` — read as dollars, a 100x error,
                  on the block whose entire purpose is the comparison. */}
              <RawTable
                rows={presentation.delta.rows}
                columns={presentation.envelopes[0]?.envelope.columns}
                caption="Difference between windows"
              />
            </section>
          )}
        </div>
      );
    case 'refusal':
      return (
        <section className="rounded-md border bg-muted/50 p-3" role="note">
          <h4 className="text-sm font-semibold">Not served</h4>
          <p className="mt-1 whitespace-pre-wrap text-sm">{presentation.refusal.message}</p>
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {presentation.refusal.reason_code}
            {presentation.refusal.metric_id ? ` · ${presentation.refusal.metric_id}` : ''}
          </p>
          {presentation.refusal.suggestion && (
            <p className="mt-1 text-xs text-muted-foreground">{presentation.refusal.suggestion}</p>
          )}
        </section>
      );
    case 'records':
      return (
        <section className="space-y-2">
          <h4 className="text-sm font-semibold">
            Source health
          </h4>
          <RawTable rows={presentation.entries} caption={record.tool} />
        </section>
      );
    case 'catalog':
    case 'describe':
    case 'acknowledged':
    case 'attempt':
      // Unreachable: rendersInAnswer() above returned null for these.
      return null;
    default:
      return (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          The tool <span className="font-mono">{presentation.tool}</span> returned a shape this
          screen does not render. Nothing was dropped silently — the raw result is in the turn
          record.
        </p>
      );
  }
}
